// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link } from '@tanstack/react-router';

// material ui icons
import HomeIcon from '@mui/icons-material/Home';

// material ui
import { Box, Button, Container, Typography } from '@mui/material';

const NotFoundComponent = () => {
  const { t } = useTranslation('not-found');
  const statusCode = '404';
  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center',
          gap: 3,
        }}
      >
        {/* 404 SVG Illustration - Abstract Environmental Theme */}
        <Box
          component="svg"
          viewBox="0 0 800 600"
          sx={{
            width: '100%',
            maxWidth: 500,
            height: 'auto',
          }}
        >
          <defs>
            <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#CED733" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#c2cd00" stopOpacity="0.3" />
            </linearGradient>
            <linearGradient id="grad2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#c2cd00" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#96b100" stopOpacity="0.4" />
            </linearGradient>
            <linearGradient id="grad3" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#CED733" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#878F00" stopOpacity="0.5" />
            </linearGradient>
          </defs>

          {/* Abstract flowing shapes in background */}
          <path
            d="M 100 150 Q 200 100 300 180 T 500 150 T 700 200"
            fill="none"
            stroke="url(#grad1)"
            strokeWidth="40"
            strokeLinecap="round"
            opacity="0.4"
          />
          <path
            d="M 150 400 Q 300 350 400 420 T 650 400"
            fill="none"
            stroke="url(#grad2)"
            strokeWidth="50"
            strokeLinecap="round"
            opacity="0.3"
          />
          <path
            d="M 50 300 Q 200 280 350 320 T 600 280"
            fill="none"
            stroke="url(#grad3)"
            strokeWidth="35"
            strokeLinecap="round"
            opacity="0.35"
          />

          {/* Abstract circular elements */}
          <circle cx="150" cy="200" r="80" fill="url(#grad1)" opacity="0.2" />
          <circle cx="620" cy="350" r="100" fill="url(#grad2)" opacity="0.18" />
          <circle cx="380" cy="480" r="60" fill="url(#grad3)" opacity="0.25" />

          {/* Organic scattered dots pattern */}
          <circle cx="250" cy="150" r="5" fill="#c2cd00" opacity="0.4" />
          <circle cx="280" cy="140" r="3" fill="#CED733" opacity="0.5" />
          <circle cx="310" cy="155" r="4" fill="#96b100" opacity="0.45" />
          <circle cx="550" cy="220" r="6" fill="#c2cd00" opacity="0.35" />
          <circle cx="520" cy="235" r="4" fill="#878F00" opacity="0.4" />
          <circle cx="180" cy="380" r="5" fill="#CED733" opacity="0.45" />
          <circle cx="200" cy="410" r="3" fill="#c2cd00" opacity="0.5" />
          <circle cx="480" cy="450" r="4" fill="#96b100" opacity="0.4" />
          <circle cx="510" cy="470" r="6" fill="#878F00" opacity="0.35" />

          {/* Abstract geometric shapes */}
          <polygon
            points="680,120 720,100 730,140 700,160"
            fill="url(#grad1)"
            opacity="0.3"
          />
          <polygon
            points="100,450 140,430 150,480 110,500"
            fill="url(#grad2)"
            opacity="0.25"
          />

          {/* Flowing organic lines */}
          <path
            d="M 200 250 C 250 240, 280 260, 320 250"
            fill="none"
            stroke="#96b100"
            strokeWidth="2"
            opacity="0.5"
          />
          <path
            d="M 450 320 C 480 310, 520 330, 560 315"
            fill="none"
            stroke="#c2cd00"
            strokeWidth="2"
            opacity="0.45"
          />

          {/* 404 Text - bold and centered */}
          <text
            x="400"
            y="320"
            fontSize="140"
            fontWeight="bold"
            fill="#788403"
            textAnchor="middle"
            fontFamily="Arial, sans-serif"
            opacity="0.9"
          >
            {statusCode}
          </text>

          {/* Subtle ring elements around text */}
          <circle
            cx="400"
            cy="280"
            r="200"
            fill="none"
            stroke="#CED733"
            strokeWidth="1"
            opacity="0.2"
          />
          <circle
            cx="400"
            cy="280"
            r="220"
            fill="none"
            stroke="#c2cd00"
            strokeWidth="1"
            opacity="0.15"
          />
        </Box>

        {/* Text Content */}
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {t('not-found-title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{
            marginBottom: "16px"
          }}>
            {t('not-found-message')}
          </Typography>
        </Box>

        {/* Action Button */}
        <Link to="/$accountId">
          <Button
            variant="contained"
            size="large"
            startIcon={<HomeIcon />}
            sx={{ borderRadius: 2 }}
          >
            {t('go-home')}
          </Button>
        </Link>
      </Box>
    </Container>
  );
};

export default NotFoundComponent;
